import { Link } from 'react-router-dom';
import { ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { PageHero } from '../shared/PageHero';
import { PressStrip } from '../shared/PressStrip';
import { AboutShelf } from './AboutShelf';
import { VisitBlock } from '../shared/VisitBlock';
import { placeholderImages } from '../../data/placeholderImages';
import { founder, salon } from '../../data/content';
import { pageActions } from '../../data/pageActions';
import { usePages } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { quietLink } from '../../ui/sections';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';

/** Alleen feiten die Sarah kan onderbouwen; geen opgeklopte getallen. Reactietijd staat op Contact. */
const facts = [{
  value: policyCopy.experience,
  label: 'in de huidzorg'
}, {
  value: 'Eén',
  label: 'cliënt tegelijk, zonder haast'
}];

/**
 * Over Sarah, als één verhaal (docs/VORMGEVING.md §3). Eén vraag: waarom zou ik háár vertrouwen
 * met mijn huid? Wie (opening, sand) · hoe het begon, met feiten, opleidingen en de perslijn
 * (zelfde vlak) · waar ik voor sta (klei, het ene bewegende beeld) · haar eigen plank (shell) ·
 * kom kennismaken (blush). Geen blok herhaalt een ander.
 *
 * Alleen over Sarah: vaste cliënten staan bij de reviews (Home, productpagina), collega's op de
 * salonpagina, adres, tijden en reactietijd op Contact.
 */
export function About() {
  const pages = usePages();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.about;
  const portrait = mediaOr(copy.hero.media, {
    image: founder.portraitPalm,
    alt: `${founder.name}, ${founder.role}`,
    focus: {
      x: 50,
      y: 30
    }
  });
  const atWork = mediaOr(copy.philosophy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling`
  });
  // De eerste alinea draagt de opening; de rest is "hoe het begon", met de laatste als slotzin.
  const [lead, ...rest] = copy.hero.paragraphs.filter(p => p.trim());
  const closing = rest.length > 1 ? rest[rest.length - 1] : undefined;
  const story = closing ? rest.slice(0, -1) : rest;
  const credentials = copy.credentials.items.filter(c => c.trim());
  return <>
      {/* Opening: zelfde opbouw als salon en protocollen; het eerste scherm is het portret en haar belofte. */}
      <PageHero titleId="about-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} data-id="element-5551" data-mp="1r292va7m4cav" />} body={lead ? fill(lead) : undefined} media={portrait} byline={{
      lead: <span className="text-ink" data-id="element-5552" data-mp="2xj4dwdl89sdm">{founder.name}</span>,
      sub: `${founder.role} · ${salon.city}`
    }} actions={/* Wie haar vertrouwt, wil zien wat ze kiest: de collectie is de hoofdknop, de afspraak de tweede. */
    <>
            <Link to="/webshop" className={btnPrimaryLg} data-id="element-5553" data-mp="37lq45fchqt70">
              {pageActions.viewCollection}
            </Link>
            {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-5554" data-mp="2ysd2np0d2j9b">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-5550" data-mp="1m1blv0yc9oay" />

      {/* Hoe het begon: het verhaal links, het bewijs rechts. Hetzelfde zandvlak als de opening. */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-28" aria-labelledby="about-story" data-id="element-5555" data-mp="2jukn4wutduv4">
        <div className="mx-auto grid max-w-content gap-12 border-t border-ink/15 px-6 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pt-16" data-id="element-5556" data-mp="2ftsfg81lby0y">
          <div className="max-w-2xl lg:col-span-7" data-id="element-5557" data-mp="i2g2m6fn9zuf">
            <h2 id="about-story" className="type-sub text-ink" data-id="element-5558" data-mp="e25t4jsn8rat">
              Hoe het begon
            </h2>
            {story.length > 0 && <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-muted" data-id="element-5559" data-mp="l6nh9dwt6pcx">
                {story.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} data-id="element-5560" data-mp="q2cj5nwawice" data-mpi={i}>{fill(p)}</p>)}
              </div>}
            {closing && <p className="mt-8 type-title italic text-ink" data-id="element-5561" data-mp="g5zg3zev8s4z">{fill(closing)}</p>}
          </div>

          <div className="lg:col-span-5" data-id="element-5562" data-mp="2eulxcu9os21">
            {/* Getal + uitleg lezen als één zin: "25 jaar in de huidzorg". */}
            <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1 lg:gap-0 lg:divide-y lg:divide-ink/15" data-id="element-5563" data-mp="75ukemj1bi6n">
              {facts.map(f => <li key={f.label} className="lg:flex lg:items-baseline lg:gap-5 lg:py-5 lg:first:pt-0" data-id="element-5564" data-mp="3onvu1jcoiz0s">
                  <span className="block type-sub text-ink lg:w-40 lg:shrink-0" data-id="element-5565" data-mp="3tj2wknu7pxo4">{f.value}</span>
                  <span className="mt-1.5 block text-[14px] leading-snug text-muted lg:mt-0" data-id="element-5566" data-mp="3fnh9im7jhnyc">{f.label}</span>
                </li>)}
            </ul>
            {credentials.length > 0 && <div className="mt-8 border-t border-ink/15 pt-6" data-id="element-5567" data-mp="310s27y71q6he">
                <h3 className="text-[14px] text-muted" data-id="element-5568" data-mp="2w0w6em8i5lcx">{fill(copy.credentials.heading)}</h3>
                <ul className="mt-3 space-y-2" data-id="element-5569" data-mp="3a0f0ib4haw68">
                  {credentials.map(c => <li key={c} className="text-[15px] leading-snug text-ink" data-id="element-5570" data-mp="35wpglyyr12rj">
                      {fill(c)}
                    </li>)}
                </ul>
              </div>}
          </div>
        </div>
        {/* Pers als bewijs bij haar verhaal, als regel onder de feiten (zonder vermeldingen niets). */}
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-5571" data-mp="3tpfp1y7g13x2">
          <PressStrip className="mt-14 lg:mt-16" data-id="element-5572" data-mp="3opbc2i5qurbl" />
        </div>
      </section>

      {/* Waar ik voor sta: het ene klei-vlak. De foto beweegt licht binnen zijn kader. */}
      <section id="filosofie" className="scroll-mt-24 bg-clay text-shell" aria-label="Filosofie" data-id="element-5573" data-mp="1k2exg5liqsk">
        <div className="grid lg:grid-cols-12" data-id="element-5574" data-mp="2vc23bv3s318k">
          <figure className="flex flex-col justify-center px-6 py-20 sm:px-10 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-5575" data-mp="2vdd73ux848zo">
            <p className="text-[14px] text-shell" data-id="element-5576" data-mp="34b5nl2eolmon">{fill(copy.philosophy.eyebrow)}</p>
            <blockquote className="mt-5 max-w-2xl type-section" data-id="element-5577" data-mp="393gjzva7qqcg">
              “{fill(copy.philosophy.claim)}.”
            </blockquote>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-shell" data-id="element-5578" data-mp="3e1i3uifgqtoz">{fill(copy.philosophy.body)}</p>
            <figcaption className="mt-8 type-title italic text-blush" data-id="element-5579" data-mp="3j37j6a9pxtiv">{founder.firstName}</figcaption>
          </figure>
          <ParallaxMedia media={atWork} sizes="(min-width: 1024px) 42vw, 100vw" range={40} className="aspect-[4/5] w-full sm:aspect-[16/11] lg:col-span-5 lg:aspect-auto lg:h-full lg:min-h-[640px]" data-id="element-5580" data-mp="3s1sxft85qmdu" />
        </div>
      </section>

      <AboutShelf copy={copy.shelf} data-id="element-5581" data-mp="1xpuqxz18g4ln" />

      {/* Kom kennismaken: het gedeelde "kom langs"-blok. Eén knop; de salonpagina en WhatsApp als rustige links. */}
      <VisitBlock titleId="about-salon" eyebrow={copy.salon.eyebrow} heading={copy.salon.heading} body={copy.salon.body} cta={copy.salon.cta} media={{
      image: placeholderImages.salonRoom,
      alt: 'Sfeerbeeld: een rustige behandelkamer met linnen in daglicht'
    }} motion="still" links={<>
            {copy.salon.salonCta && <Link to="/behandelingen" className={`${quietLink} text-ink`} data-id="element-5583" data-mp="245bnmaxixfx3">
                {fill(copy.salon.salonCta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5584" data-mp="200ls4n6s23cp" />
              </Link>}
            {copy.salon.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-5585" data-mp="oob6d0qgb7hr">
                <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5586" data-mp="jndqwvwtsrjy" />
                {fill(copy.salon.secondaryCta)}
                <span className="sr-only" data-id="element-5587" data-mp="2jssqdzwaxxz5">{shopLabels.viaWhatsApp}</span>
              </a>}
          </>} data-id="element-5582" data-mp="1lchg2228cq2w" />
    </>;
}